import { initializeApp } from "firebase/app";
import {
  getFirestore,
  collection,
  getDocs,
  addDoc,
  setDoc,
  deleteDoc,
  doc,
  query,
  limit,
} from "firebase/firestore";
import { deleteObject, getStorage, ref, StorageReference, uploadBytes } from "firebase/storage";

import { Message } from "../types";
import { StudentFormData } from "../AdmissionForm/StudentDataFormProvider";
import { v4 as uuid } from "uuid";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_API_KEY,
  authDomain: process.env.REACT_APP_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_PROJECT_ID,
  storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_APP_ID,
  measurementId: process.env.REACT_APP_MEASUREMENT_ID,
};

export const app = initializeApp(firebaseConfig);
export const db = getFirestore(app);
export const storage = getStorage(app);

const messagesDbName = process.env.REACT_APP_M_DB_NAME;
const admissionFormsDbName = process.env.REACT_APP_AF_DB_NAME;

// Messages
//#region

export async function getMessages() {
  const messagesRef = collection(db, messagesDbName!);
  const messagesQuery = query(messagesRef, limit(100));
  const messagesSnapshot = await getDocs(messagesQuery);
  const messagesList = messagesSnapshot.docs.map((doc) => {
    return { ...doc.data(), id: doc.id };
  });

  return messagesList;
}

interface createMessageInput {
  data: Message;
}

export async function createMessage({ data }: createMessageInput) {
  addDoc(collection(db, messagesDbName!), data)
    .then((r) => {
      return true;
    })
    .catch((e) => {
      return false;
    });
}

interface setMessageInput {
  id: string;
  data: Message;
}
export async function setMessage({ id, data }: setMessageInput) {
  setDoc(doc(db, messagesDbName!, id), data)
    .then((r) => {
      return true;
    })
    .catch((e) => {
      return false;
    });
}

interface delMessageInput {
  id: string;
}

export async function delMessage({ id }: delMessageInput) {
  deleteDoc(doc(db, messagesDbName!, id))
    .then((r) => {
      return true;
    })
    .catch((e) => {
      return false;
    });
}

//#endregion

export enum Gender {
  male = "male",
  female = "female",
}

type FileUrl = string;

export type AdmissionFormData = {
  firstName: string;
  lastName: string;
  nationality: string;
  homeSpokenLanguage: string;
  dateOfBirth: Date;
  gender: Gender;
  specialRequirements: string;
  lastSchoolAttended?: string;
  leavingCertificate?: FileUrl;
  address: string;
  emergencyContactName: string;
  emergencyContactNumber: string;
  doctorFullName: string;
  doctorContactNumber: string;
  doctorHospitalName: string;
  passportPicture: FileUrl;
  birthCertificate: FileUrl;
  passportFrontPage: FileUrl;
  passportLastPage: FileUrl;
  drcVisa: FileUrl;
  acknowledgement: boolean;
  motherFullName: string;
  motherContactNumber: string;
  motherEmail: string;
  motherQualification: string;
  fatherFullName: string;
  fatherContactNumber: string;
  fatherEmail: string;
  fatherQualification: string;
  submittedAt: string;
};

type FilesToUpload =
  | "birthCertificate"
  | "passportFrontPage"
  | "passportLastPage"
  | "passportPicture"
  | "drcVisa"
  | "leavingCertificate";

type RawFiles = {
  [key in FilesToUpload]: File;
};

type FilePaths = {
  [key in FilesToUpload]: string;
};

export const createAdmissionForm = async (studentForm: StudentFormData): Promise<string | undefined> => {
  const admissionFormDoc = doc(collection(db, admissionFormsDbName!));

  const files: RawFiles = {
    birthCertificate: studentForm.birthCertificate.item(0)!,
    passportFrontPage: studentForm.passportFrontPage.item(0)!,
    passportLastPage: studentForm.passportLastPage.item(0)!,
    passportPicture: studentForm.passportPicture.item(0)!,
    drcVisa: studentForm.drcVisa.item(0)!,
    leavingCertificate: studentForm.leavingCertificate?.item(0)!,
  };

  const uploadedRefs: StorageReference[] = [];
  const folder = `admissionForm/${admissionFormDoc.id}`;

  try {
    const filePaths = {} as FilePaths;
    await Promise.all(
      Object.entries(files)
        .filter(([filename, file]) => file !== null)
        .map(async ([filename, file]) => {
          const fileId = uuid();
          const ext = file.name.split(".").pop();
          const fileRef = ref(storage, `${folder}/${fileId}.${ext}`);
          const metadata = file.type ? { contentType: file.type } : undefined;
          await uploadBytes(fileRef, file, metadata);
          uploadedRefs.push(fileRef);
          filePaths[filename as FilesToUpload] = fileRef.fullPath;
        })
    );

    const admissionForm = toAdmissionForm(studentForm, filePaths);
    // console.log(admissionForm);
    setDoc(doc(db, "admissionFormsProduction786110", admissionFormDoc.id), admissionForm).catch((e) => {
      // console.log(">> error", e);
      return false;
    });

    return admissionFormDoc.id;
  } catch (err) {
    await Promise.all(uploadedRefs.map((r) => deleteObject(r)));
    return undefined;
  }
};

const toAdmissionForm = (studentForm: StudentFormData, filepaths: FilePaths): AdmissionFormData => {
  return {
    firstName: studentForm.firstName,
    lastName: studentForm.lastName,
    nationality: studentForm.nationality,
    homeSpokenLanguage: studentForm.homeSpokenLanguage,
    dateOfBirth: studentForm.dateOfBirth,
    gender: studentForm.gender,
    specialRequirements: studentForm.specialRequirements,
    lastSchoolAttended: studentForm.lastSchoolAttended,
    address: studentForm.address,
    emergencyContactName: studentForm.emergencyContactName,
    emergencyContactNumber: studentForm.emergencyContactNumber,
    doctorFullName: studentForm.doctorFullName,
    doctorContactNumber: studentForm.doctorContactNumber,
    doctorHospitalName: studentForm.doctorHospitalName,
    acknowledgement: studentForm.acknowledgement,
    motherFullName: studentForm.motherFullName,
    motherContactNumber: studentForm.motherContactNumber,
    motherEmail: studentForm.motherEmail,
    motherQualification: studentForm.motherQualification,
    fatherFullName: studentForm.fatherFullName,
    fatherContactNumber: studentForm.fatherContactNumber,
    fatherEmail: studentForm.fatherEmail,
    fatherQualification: studentForm.fatherQualification,
    birthCertificate: filepaths.birthCertificate ?? null,
    drcVisa: filepaths.drcVisa ?? null,
    leavingCertificate: filepaths.leavingCertificate ?? null,
    passportFrontPage: filepaths.passportFrontPage ?? null,
    passportLastPage: filepaths.passportLastPage ?? null,
    passportPicture: filepaths.passportPicture ?? null,
    submittedAt: new Date().toISOString(),
  };
};
